import { ReactNode, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { useTranslation } from 'react-i18next';
import { closeSidePanel } from '@customer/store/slices/globalSlice';
import { useNavigate, useParams } from 'react-router-dom';
import useTabs, { TabConfig, TabModel } from '@cas/components/tabs/tabs/useTabs';
import InvestigationAssetActivity from './activity/InvestigationAssetActivity';
import InvestigationAssetHealth from './health/InvestigationAssetHealth';
import {
  InvestigationAssetTrackCompleteness,
  useGetInvestigationQuery,
} from '@customer/store/apis/investigationsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { SystemTypePpu } from '@customer/store/apis/assetsApi';
import { GeofenceData } from '@customer/hooks/assets/useAssetGeofenceTableData';

interface Return {
  asset: {
    name?: string;
    hw?: string;
    mismatch: boolean;
    groups?: number[];
    cmp?: InvestigationAssetTrackCompleteness;
  };
  tabsProps: {
    tabs: TabModel[];
    activeTab?: number;
    onSelectTab: (index: number) => void;
  };
  btnProps?: {
    onClick: () => void;
  };
  activeView: ReactNode;
}

export const useInvestigationAssetPanel = (): Return => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { investigationId } = useParams();
  const { sidePanelModelId: assetId, sidePanelTab } = useAppSelector(state => state.global);
  const siteId = useAppSelector(state => state.auth.siteId);

  const isSkipQuery =
    typeof investigationId === 'undefined' ||
    typeof siteId === 'undefined' ||
    typeof assetId === 'undefined';

  const { currentData: investigation } = useGetInvestigationQuery(
    isSkipQuery ? skipToken : { siteId, investigationId },
    { refetchOnMountOrArgChange: 60 },
  );

  useEffect(() => {
    if (investigationId === undefined) {
      dispatch(closeSidePanel());
    }
  }, [dispatch, investigationId]);

  const investigationAsset = investigation?.assets?.find(a => a.asset === Number(assetId));

  const geofenceData = useMemo<GeofenceData[]>(() => {
    if (!investigationAsset) {
      return [];
    }

    return [
      {
        where: t('GeofenceAssetVersion'),
        version: investigationAsset?.geoVer ?? 0,
        ts: investigationAsset?.geoTs ?? undefined,
        error: !(investigationAsset?.geoUpd ?? false),
      },
      {
        where: t('GeofenceSiteVersion'),
        version: investigation?.siteGeoVer ?? 0,
        ts: investigation?.siteGeoTs ?? undefined,
        error: false,
      },
    ];
  }, [investigationAsset, t, investigation?.siteGeoVer, investigation?.siteGeoTs]);

  const tabConfigs = useMemo<TabConfig[]>(() => {
    const items: TabConfig[] = [];

    if (investigationAsset?.hw !== SystemTypePpu) {
      const faults = {
        count: investigationAsset?.fltCnt ?? 0,
        items: investigationAsset?.flts ?? [],
      };

      let anyLevelOne = false;
      if (investigationAsset?.flts) {
        anyLevelOne = investigationAsset.flts.findIndex(f => f.lvl === 1) >= 0;
      }

      items.push({
        title: t('CasHealth'),
        view: (
          <InvestigationAssetHealth
            geofenceData={geofenceData}
            geofenceUpToDate={investigationAsset?.geoUpd ?? false}
            ts={investigation?.ts}
            configUpToDate={investigationAsset?.cfgUpd ?? null}
            faults={faults}
          />
        ),
        isDotted: anyLevelOne,
      });
    }

    items.push({
      title: t('Activity'),
      view: <InvestigationAssetActivity assetId={assetId} investigationId={investigation?.id} />,
      isDotted: false,
    });

    return items;
  }, [investigationAsset, t, assetId, investigation?.id, investigation?.ts, geofenceData]);

  const { tabs, activeTab, activeView, onSelectTab } = useTabs({
    tabConfigs,
    defaultTab: sidePanelTab,
  });

  const buttonOnClick = () => {
    dispatch(closeSidePanel());

    if (investigationAsset) {
      navigate(`/asset/${investigationAsset.asset}/interactions`);
    }
  };

  return {
    asset: {
      name: investigationAsset?.n,
      hw: investigationAsset?.hw,
      mismatch: !(investigationAsset?.stsNam ?? false),
      groups: investigationAsset?.ags,
      cmp: investigationAsset?.cmp,
    },
    tabsProps: {
      tabs,
      activeTab,
      onSelectTab: onSelectTab,
    },
    btnProps: {
      onClick: buttonOnClick,
    },
    activeView,
  };
};

export default useInvestigationAssetPanel;
